import { useUpdateReassignmentMutation } from '@cartwheel/api/v1'
import {
  Announcement,
  AnnouncementType,
  ReassignmentInProgressCard,
  ReassignmentRequestedCard,
  ReassignmentScheduledCard,
} from '@cartwheel/components'
import { useState } from 'react'
import { ReassignmentDetailWrapper } from './reassignment-detail-modal'

type ReassignmentStatus = 'initiated' | 'approved' | 'assigned_to_cc'
type ReassignmentState = 'requested' | 'in_progress' | 'scheduled'

interface BaseReassignmentCardProps {
  state: ReassignmentState
  status: ReassignmentStatus
  reassignmentId: string
  effectiveDate: string
  requesterType: string
  canTakeAction: boolean
  isCaseload?: boolean
  caseloadClientCount?: number
}

interface RequestedStateProps extends BaseReassignmentCardProps {
  state: 'requested'
}

interface InProgressStateProps extends BaseReassignmentCardProps {
  state: 'in_progress'
  canCancel?: boolean
  reassignmentPath?: string
  clinicianName?: string
  clinicianEmail?: string
  showSelfScheduleLink?: boolean
  selfScheduleUrl?: string
  selfScheduleDisabledReasons?: string[]
  cancellableClientCount?: number
}

interface ScheduledStateProps extends BaseReassignmentCardProps {
  state: 'scheduled'
  canCancel?: boolean
  clinicianName?: string
  clinicianEmail?: string
  cancellableClientCount?: number
}

type ReassignmentCardWrapperProps = RequestedStateProps | InProgressStateProps | ScheduledStateProps

export function ReassignmentCardWrapper(props: ReassignmentCardWrapperProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [clinicianEmailCopied, setClinicianEmailCopied] = useState(false)

  const cancelMutation = useUpdateReassignmentMutation({
    onSuccess: () => {
      const url = new URL(window.location.href)
      url.searchParams.set('success', 'Reassignment Cancelled')
      window.location.assign(url.toString())
    },
  })

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleReassignClick = () => {
    if (props.state === 'in_progress' && props.reassignmentPath) {
      window.location.href = props.reassignmentPath
    }
  }

  const handleCancelClick = () => {
    const cancellableCount =
      (props.state === 'in_progress' || props.state === 'scheduled') && props.cancellableClientCount
        ? props.cancellableClientCount
        : 1
    const otherClientsAffected = cancellableCount - 1

    let confirmMessage: string
    if (otherClientsAffected > 0) {
      confirmMessage = `This will also cancel the reassignment for ${otherClientsAffected} other client${otherClientsAffected === 1 ? '' : 's'}. This action cannot be undone.`
    } else {
      confirmMessage =
        'Are you sure you want to cancel this reassignment? This action cannot be undone.'
    }

    if (!window.confirm(confirmMessage)) return

    cancelMutation.mutate({
      path: { id: props.reassignmentId },
      body: { reassignment_action: 'cancelled' },
    })
  }

  const handleCopyClinicianEmail = async () => {
    const clinicianEmail =
      props.state === 'in_progress' || props.state === 'scheduled'
        ? props.clinicianEmail
        : undefined

    if (!clinicianEmail) return

    try {
      await navigator.clipboard.writeText(clinicianEmail)
      setClinicianEmailCopied(true)
      setTimeout(() => setClinicianEmailCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy email:', error)
    }
  }

  const renderCard = () => {
    switch (props.state) {
      case 'requested':
        return (
          <ReassignmentRequestedCard
            effectiveDate={props.effectiveDate}
            requesterType={props.requesterType}
            canTakeAction={props.canTakeAction}
            onReviewClick={props.canTakeAction ? handleOpenModal : undefined}
            onViewDetailsClick={props.canTakeAction ? undefined : handleOpenModal}
          />
        )

      case 'in_progress':
        return (
          <ReassignmentInProgressCard
            status={props.status as 'approved' | 'assigned_to_cc'}
            effectiveDate={props.effectiveDate}
            requesterType={props.requesterType}
            canTakeAction={props.canTakeAction}
            canCancel={props.canCancel}
            onReassignClick={props.canTakeAction ? handleReassignClick : undefined}
            onCancelClick={props.canCancel ? handleCancelClick : undefined}
            onViewDetailsClick={props.canTakeAction ? undefined : handleOpenModal}
            clinicianName={props.clinicianName}
            clinicianEmail={props.clinicianEmail}
            onClinicianNameClick={handleCopyClinicianEmail}
            clinicianEmailCopied={clinicianEmailCopied}
            showSelfScheduleSection={props.showSelfScheduleLink}
            selfScheduleUrl={props.selfScheduleUrl}
            selfScheduleDisabledReasons={props.selfScheduleDisabledReasons}
          />
        )

      case 'scheduled':
        return (
          <ReassignmentScheduledCard
            status={props.status as 'approved' | 'assigned_to_cc'}
            effectiveDate={props.effectiveDate}
            requesterType={props.requesterType}
            canCancel={props.canCancel}
            onCancelClick={props.canCancel ? handleCancelClick : undefined}
            onViewDetailsClick={handleOpenModal}
            clinicianName={props.clinicianName}
            clinicianEmail={props.clinicianEmail}
            onClinicianNameClick={handleCopyClinicianEmail}
            clinicianEmailCopied={clinicianEmailCopied}
          />
        )
    }
  }

  return (
    <>
      {props.isCaseload && (
        <Announcement
          type={AnnouncementType.Warning}
          message={`This reassignment covers ${props.caseloadClientCount} clients. Actions taken here will affect all of them.`}
        />
      )}
      {renderCard()}
      <ReassignmentDetailWrapper
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        reassignmentId={props.reassignmentId}
        canReview={props.canTakeAction && props.state === 'requested'}
      />
    </>
  )
}

export default ReassignmentCardWrapper
